import { AuditResourceType } from '_lib/api';
import { useAppDispatch } from '_store';
import auditReducer from '_store/audit/auditReducers';
import { HISTORY_LIMIT } from '_store/audit/auditTypes';
import { auditHistorySelector } from '_store/audit/selectors';
import { useLocalization } from '@fluent/react';
import { Button, Typography } from '@material-ui/core';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';

import { Accordion } from '../Accordion';
import AuditHistoryList from './List';
import styles from './styles';

export interface AuditHistorySectionProps {
  resourceType: AuditResourceType;
  resourceId: string;
}

const AuditHistorySection: FC<AuditHistorySectionProps> = ({ resourceType, resourceId }) => {
  const classes = styles();
  const { l10n } = useLocalization();
  const dispatch = useAppDispatch();

  const history = useSelector(auditHistorySelector(resourceType, resourceId));

  const load = () =>
    dispatch(
      auditReducer.actions.historyLogsRequest({ resourceType, resourceId, limit: HISTORY_LIMIT }),
    );

  // Lazy: browsing map markers would otherwise fire one audit request per click for a panel
  // that sits below the fold and is rarely read. Cached for the session once loaded.
  const loadIfNeeded = () => {
    if (history.fetchedAt === 0 && !history.loading) {
      load();
    }
  };

  return (
    <Accordion title={l10n.getString('audit-history-title')} onExpanded={loadIfNeeded}>
      <AuditHistoryList
        entries={history.results}
        total={history.total}
        loading={history.loading}
        error={history.error}
      />

      <div className={classes.footer}>
        <Button
          variant="text"
          color="primary"
          className={classes.refresh}
          onClick={load}
          disabled={history.loading}
        >
          {l10n.getString('audit-refresh')}
        </Button>
      </div>

      {resourceType === AuditResourceType.RSU ? (
        <Typography variant="caption" className={classes.note}>
          {l10n.getString('audit-rsu-bulk-note')}
        </Typography>
      ) : null}
    </Accordion>
  );
};

export default React.memo(AuditHistorySection);
